import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, User, Video } from "lucide-react";
import { nameRejection, normalizeIndianMobile } from "@/lib/joinValidation";
import { DesignStage, At } from "@/components/DesignStage";

const WaGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.348.52-.522.174-.174.232-.298.347-.497.115-.199.058-.371-.024-.522-.082-.15-.67-1.612-.921-2.207-.192-.456-.397-.463-.57-.47-.148-.007-.32-.009-.492-.009a.944.944 0 0 0-.686.322c-.236.257-.9.881-.9 2.146s.922 2.487 1.05 2.66c.128.174 1.813 2.769 4.393 3.884.614.265 1.093.423 1.467.541.616.196 1.177.168 1.62.102.494-.074 1.522-.622 1.737-1.223.215-.6.215-1.115.15-1.223-.064-.107-.239-.172-.536-.321Z" />
    <path d="M12.002 2C6.478 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.95-1.3A9.953 9.953 0 0 0 12.002 22C17.526 22 22 17.523 22 12S17.526 2 12.002 2Zm0 18.13a8.1 8.1 0 0 1-4.126-1.129l-.296-.176-3.075.807.82-3.002-.193-.309A8.088 8.088 0 0 1 3.87 12c0-4.484 3.648-8.13 8.132-8.13 4.484 0 8.13 3.646 8.13 8.13 0 4.484-3.646 8.13-8.13 8.13Z" />
  </svg>
);

/**
 * The entry screen. The artwork carries the logo, the heading and the field
 * labels; the two inputs and the button are real, crisp DOM on top of it.
 *
 * Coordinates measured off the 720x1280 artwork:
 *   name pill  x 82-638  y 532-604
 *   number pill x 82-638 y 690-760
 *   join button x 84-637 y 795-880  (drawn over, so it renders sharp)
 *
 * Errors are only ever shown once a student LEAVES a field — telling someone
 * their name is too short while they're typing the first letter of it is just
 * rude.
 */
export const JoinScreen = ({
  name,
  setName,
  whatsapp,
  setWhatsapp,
  onSubmit,
  loading,
  needsName,
  needsWhatsapp,
  error,
}) => {
  const [touched, setTouched] = useState({ name: false, wa: false });

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const nameBad = needsName ? nameRejection(name) : null;
  const waBad = needsWhatsapp && !normalizeIndianMobile(whatsapp);
  // Only tint a field a student actually typed something wrong into. An empty
  // field they merely tapped past is not an error yet.
  const nameWrong = !!(nameBad && name.trim());
  const waWrong = !!(waBad && whatsapp.replace(/\D/g, "").length > 0);
  const ready = !nameBad && !waBad;

  const blur = (field, message) => {
    setTouched((t) => ({ ...t, [field]: true }));
    if (message) toast.error(message);
  };

  const pill =
    "w-full h-full rounded-full text-white outline-none transition-colors " +
    "placeholder:text-white/25 font-display";

  // The artwork already draws a green outline around each field. Adding a red
  // ring on top of it drew a SECOND line beside the green one — so an invalid
  // field is shown by tinting the inside of the pill instead.
  const fieldTone = (bad, isTouched) =>
    bad && isTouched ? "bg-[#210b0b] text-red-100 placeholder:text-red-200/25" : "bg-[#04120c]";

  return (
    <DesignStage bg="/brand/screens/join@2x.webp" testid="join-screen">
      {/* Everything AROUND the form is dimmed so a student's eye lands on it
          first. The hole covers the card AND the green reflection that spills
          below the JOIN CLASS button — both belong to the form and keep the
          artwork's original colours — and its edge is feathered so there is
          no visible seam. */}
      <At l={3} t={21} w={94} h={61} className="pointer-events-none z-0">
        <div
          className="w-full h-full rounded-[10cqw]"
          style={{ boxShadow: "0 0 14cqw 9999px rgba(0,0,0,0.6)" }}
          data-testid="join-focus-dim"
        />
      </At>
      <form onSubmit={onSubmit} data-testid="join-form" className="absolute inset-0">
        {needsName && (
          <At l={11.39} t={41.56} w={77.22} h={5.63}>
            <div className="relative w-full h-full">
              <User
                className="absolute left-[5%] top-1/2 -translate-y-1/2 text-emerald-300/80 pointer-events-none"
                style={{ width: "4.2cqw", height: "4.2cqw" }}
                strokeWidth={1.7}
              />
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => blur("name", name.trim() ? nameBad : null)}
                placeholder="Aarav Kumar"
                required
                maxLength={40}
                className={`${pill} ${fieldTone(nameWrong, touched.name)}`}
                style={{ paddingLeft: "12.5%", paddingRight: "5%", fontSize: "3.4cqw" }}
                data-testid="join-name-input"
              />
            </div>
          </At>
        )}

        {needsWhatsapp && (
          <At l={11.39} t={53.91} w={77.22} h={5.47}>
            <div className="relative w-full h-full">
              <WaGlyph className="absolute left-[5%] top-1/2 -translate-y-1/2 text-emerald-300/80 pointer-events-none w-[4.4cqw] h-[4.4cqw]" />
              <input
                type="tel"
                inputMode="numeric"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 14))}
                onBlur={() =>
                  blur(
                    "wa",
                    whatsapp.replace(/\D/g, "").length
                      ? waBad
                        ? "Please enter your 10-digit WhatsApp number"
                        : null
                      : null
                  )
                }
                placeholder="9876543210"
                required
                className={`${pill} ${fieldTone(waWrong, touched.wa)}`}
                style={{ paddingLeft: "12.5%", paddingRight: "5%", fontSize: "3.4cqw" }}
                data-testid="join-whatsapp-input"
              />
            </div>
          </At>
        )}

        {/* Drawn as a real button so the text stays sharp on a retina phone.
            It sits slightly outside the artwork's own button so that one is
            fully covered, and stays dim until both fields are valid. */}
        <At l={11.25} t={61.87} w={77.8} h={7.1}>
          <button
            type="submit"
            disabled={!ready || loading}
            className={`w-full h-full rounded-full flex items-center justify-center gap-[3.5cqw] font-display transition-colors ${
              ready ? "bg-[#13dda2] text-black active:brightness-95" : "bg-[#0e3d31] text-white/40 cursor-not-allowed"
            }`}
            style={ready ? { boxShadow: "0 1.4cqw 3.6cqw -1.6cqw rgba(19,221,162,0.65)" } : undefined}
            data-testid="join-submit-btn"
          >
            {loading ? (
              <Loader2 className="animate-spin w-[4.4cqw] h-[4.4cqw]" />
            ) : (
              <>
                <Video className="w-[4.6cqw] h-[4.6cqw]" strokeWidth={0} fill="currentColor" />
                <span className={`block w-px h-[45%] ${ready ? "bg-black/25" : "bg-white/15"}`} />
                <span className="font-extrabold" style={{ fontSize: "3.2cqw", letterSpacing: "0.2em" }}>
                  JOIN CLASS
                </span>
              </>
            )}
          </button>
        </At>
      </form>
    </DesignStage>
  );
};
